// How many of a column header's indicators fit beside its name. The name comes first: it keeps
// every word whole, in up to three lines, and the indicators give way, the last first.

export const INDICATOR_SIZE = 14
export const INDICATOR_GAP = 4
// The indicators' right margin, between the last one and the ⋮.
export const INDICATORS_MARGIN = 4
export const NAME_MAX_LINES = 3

// The room the first `count` indicators take, margin included.
export const indicatorsWidth = (count: number): number =>
  count > 0 ? count * INDICATOR_SIZE + (count - 1) * INDICATOR_GAP + INDICATORS_MARGIN : 0

// The lines a name wraps to in `width`, word by word, or Infinity when a word alone is wider.
export const linesNeeded = (wordWidths: number[], spaceWidth: number, width: number): number => {
  let lines = 0
  let lineWidth = 0
  for (const word of wordWidths) {
    if (word > width) return Infinity
    if (lines > 0 && lineWidth + spaceWidth + word <= width) {
      lineWidth += spaceWidth + word
    } else {
      lines += 1
      lineWidth = word
    }
  }
  return lines
}

// The most indicators, out of `total`, that leave the name `room` to show whole, where `room` is
// the width the name has with none shown.
export const indicatorsThatFit = (total: number, room: number, wordWidths: number[], spaceWidth: number): number => {
  for (let count = total; count > 0; count--) {
    if (linesNeeded(wordWidths, spaceWidth, room - indicatorsWidth(count)) <= NAME_MAX_LINES) return count
  }
  return 0
}

let canvas: HTMLCanvasElement | undefined

// The widths of a name's words, and of a space, in the name's own font.
export const measureWords = (text: string, font: string): { wordWidths: number[]; spaceWidth: number } | null => {
  canvas ??= document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return null
  context.font = font
  return {
    wordWidths: text
      .trim()
      .split(/\s+/)
      .map((word) => context.measureText(word).width),
    spaceWidth: context.measureText(' ').width
  }
}
